import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import { useEffect, useState } from 'react';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ChatBot from './components/chat/ChatBot';
import LocalConsentBanner from './components/consent/LocalConsentBanner';
import './assets/css/App.css';
import TermsConditions from './pages/legal/TermsConditions';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';
import RefundPolicy from './pages/legal/RefundPolicy';

// Navigation state management happens in the Layout component below

// Custom hook for handling page metadata
const usePageMetadata = (title, description) => {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update meta description
    const metaDescriptionTag = document.querySelector('head > meta[name="description"]');
    if (metaDescriptionTag) {
      metaDescriptionTag.content = description;
    }
  }, [title, description]);
};

// Lazy load components for better performance
const Home = lazy(() => import('./pages/home/Home'));
const About = lazy(() => import('./pages/about/About'));
const Contact = lazy(() => import('./pages/contact/Contact'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Products = lazy(() => import('./pages/products/Products'));
// const Blog = lazy(() => import('./pages/blog/Blog'));
// const BlogPost = lazy(() => import('./pages/blog/BlogPost'));
const Qrcode = lazy(() => import('./pages/qrcode/Qrcode'))

// Product components
const CSDLC = lazy(() => import('./pages/products/CSDLC'));
const CSDLCAI = lazy(() => import('./pages/products/CSDLC-AI'));
const HCDLCAI = lazy(() => import('./pages/products/HCDLC-AI'));
const AISDLC = lazy(() => import('./pages/products/AISDLC'));
const BIMigrator = lazy(() => import('./pages/products/BIMigrator'));
const Devopsbuddy = lazy(() => import('./pages/products/Devopsbuddy'));
const RapidMediCapture = lazy(() => import('./pages/products/RapidMediCapture'));
const EducationalInformational = lazy(() => import('./pages/products/educational-informational'));
const HealthcareClinicalTrials = lazy(() => import('./pages/products/healthcare-clinical-trials'));
const BusinessIntelligence = lazy(() => import('./pages/products/business-intelligence'));
const Careers = lazy(() => import('./pages/Careers/Careers'));


// Layout component to wrap all pages
const Layout = ({ children }) => {
  const location = useLocation();
  const [isLocalhost, setIsLocalhost] = useState(false);
  
  useEffect(() => {
    // Check if we're on localhost
    setIsLocalhost(
      window.location.hostname === 'localhost' || 
      window.location.hostname === '127.0.0.1'
    );
    
    const lastPath = sessionStorage.getItem('lastPath');
    const currentPath = location.pathname;
    
    if (lastPath !== currentPath) {
      sessionStorage.setItem('lastPath', currentPath);
    }
  }, [location.pathname]);
  
  // Handle consent banner accept action
  const handleConsentAccept = () => {
    // Log debug information about cookies
    console.log('Consent accepted, current cookies:', document.cookie);
    
    // Initialize chat with new consent
    const initializeChat = async () => {
      try {
        // We need to import dynamically to avoid circular dependencies
        const { initializeChat } = await import('./services/chatService');
        const result = await initializeChat();
        console.log('Chat initialized after consent:', result);
        
        // Check if cookie was set after initialization
        setTimeout(() => {
          console.log('Cookies after initialization:', document.cookie);
          console.log('samyama_chat_id exists:', document.cookie.includes('samyama_chat_id'));
        }, 1000);
      } catch (error) {
        console.error('Failed to initialize chat after consent:', error);
      }
    };
    
    initializeChat();
  };

  return (
    <>
      <Header />
      <main className="main-content">
        <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
          {children}
        </Suspense>
      </main>
      <Footer />
      <Suspense fallback={<div className="chat-loading-spinner">Chat loading...</div>}>
        <ChatBot />
      </Suspense>
      
      {/* Only show the local consent banner on localhost */}
      {isLocalhost && <LocalConsentBanner onAccept={handleConsentAccept} />}
    </>
  );
};

function App() {
  const action = useNavigationType();
  const location = useLocation();
  const pathname = location.pathname;

  // Scroll to top on navigation
  useEffect(() => {
    if (action !== "POP") {
      window.scrollTo(0, 0);
    }
  }, [action, pathname]);

  // Define metadata for each route
  const metadata = {
    "/about": {
      title: "Samyama.AI | About",
      description: "Learn about Samyama's evolution from VaidhyaMegha Pvt. Ltd. to a future-focused AI brand driving innovation through intelligent digital solutions."
    },
    "/contact": {
      title: "Samyama.AI | Contact",
      description: "Get in touch with Samyama for product inquiries, partnerships, or support. We're ready to help you build AI-powered digital success."
    },
    "/careers": {
      title: "Samyama.AI | Careers",
      description: "Join Samyama's mission to build intelligent, AI-driven technology. Explore open roles and be part of a fast-growing, innovation-led team."
    },
    "/products": {
      title: "Samyama.AI | Products",
      description: "Discover AI-powered products by Samyama, built to simplify BI workflows, streamline DevOps, and enable scalable, intelligent automation."
    },
    "/qr": {
      title: "Samyama.AI | QR",
      description: "Access all official Samyama resources in one place. Scan or share this page to connect with our website, products, and social channels instantly."
    },
    "/products/bimigrator": {  
      title: "Samyama.AI | BI Migrator",
      description: "Samyama's BI Migrator automates dashboard migration across platforms like Tableau and Power BI—faster, smarter, and cost-effective."
    },
    "/products/devops-buddy": {
      title: "Samyama.AI | DevOps Buddy",
      description: "DevOps Buddy by Samyama is a unified dashboard for GitHub, AWS, support tickets, and productivity tracking—all in one intelligent platform."
    },
    "/products/rapid-medi-capture": {
      title: "Samyama.AI | Rapid MediCapture",
      description: "Rapid MediCapture by Samyama is a smart EDC platform for structured, efficient data capture—streamlining forms, validation, and patient records."
    } ,
    "/products/csdlc": {
      title: "Samyama.AI | CSDLC",
      description: "CSDLC by Samyama is an AI-powered platform for clinical trial analysis—offering study comparison, trend insights, and predictive risk intelligence."
    },
    "/products/csdlc-ai": {
      title: "Samyama.AI | CSDLC AI",
      description: "CSDLC-AI by Samyama enhances clinical trial design with AI-powered predictions, study mapping, and data-driven decision support."
    } ,
    "/products/aisdlc": {
      title: "Samyama.AI | AISDLC",
      description: "AISDLC is Samyama's AI knowledge and research platform offering curated artifacts, resources, and insights for intelligent system development."
    },
    "/products/hcdlc-ai": {
      title: "Samyama.AI | HCDLC AI",
      description: "HCDLC-AI by Samyama is an AI-powered platform for early-stage disease analysis, enabling faster insights from structured health data and diagnostic patterns."
    }
  };

  const { title, description } = metadata[location.pathname] || {
    title: "Samyama.AI | Home",
    description: "Explore Samyama.ai – a technology-first brand building AI-powered platforms for BI automation, DevOps intelligence, and digital transformation."
  };

  usePageMetadata(title, description);

  // Fallback component while lazy loading
  const LoadingFallback = () => (
    <div className="page-loading">
      <div className="loader"></div>
      <p>Loading...</p>
    </div>
  );

  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        <Route path="/" element={<Layout><Home /></Layout>} />
        <Route path="/home" element={<Layout><Home /></Layout>} />
        <Route path="/index" element={<Layout><Home /></Layout>} />
        <Route path="/about" element={<Layout><About /></Layout>} />
        <Route path="/products" element={<Layout><Products /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        <Route path="/products/educational-informational" element={<Layout><EducationalInformational /></Layout>} />
        <Route path="/products/healthcare-clinical-trials" element={<Layout><HealthcareClinicalTrials /></Layout>} />
        <Route path="/products/business-intelligence" element={<Layout><BusinessIntelligence /></Layout>} />
        <Route path="/products/aisdlc" element={<Layout><AISDLC /></Layout>} />
        <Route path="/products/csdlc" element={<Layout><CSDLC /></Layout>} />
        <Route path="/products/csdlc-ai" element={<Layout><CSDLCAI /></Layout>} />
        <Route path="/products/hcdlc-ai" element={<Layout><HCDLCAI /></Layout>} />
        <Route path="/products/rapid-medi-capture" element={<Layout><RapidMediCapture /></Layout>} />
        <Route path="/products/bimigrator" element={<Layout><BIMigrator /></Layout>} />
        <Route path="/products/devops-buddy" element={<Layout><Devopsbuddy /></Layout>} />
        <Route path="/Terms-And-Conditions" element={<Layout><TermsConditions /></Layout>} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
        <Route path="/Refund-and-cancellation-policy" element={<Layout><RefundPolicy /></Layout>} />
        <Route path="/qr" element={<Layout><Qrcode /></Layout>} />
        {/* <Route path="/blog" element={<Layout><Blog /></Layout>} />
        <Route path="/blog/:id" element={<Layout><BlogPost /></Layout>} /> */}
        <Route path="/careers" element={<Layout><Careers /></Layout>} />
        <Route path="*" element={<Layout><NotFound /></Layout>} />
      </Routes>
    </Suspense>
  );
}

export default App;
